import { useEffect, useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronLeft,
  ChevronRight,
  Columns3,
  Download,
  Eye,
  Filter,
  GripVertical,
  ListFilter,
  Pin,
  PinOff,
  RefreshCw,
  Rows3,
  Save,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const densityClasses = {
  compact: 'px-4 py-2.5',
  comfortable: 'px-5 py-3.5',
  spacious: 'px-5 py-5',
};

const defaultGetKey = (row) => row._id ?? row.id;

function asSearchText(value) {
  if (value == null) return '';
  if (['string', 'number', 'boolean'].includes(typeof value)) return String(value);
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(asSearchText).join(' ');
  if (typeof value === 'object') return Object.values(value).map(asSearchText).join(' ');
  return '';
}

function getSortValue(row, column) {
  if (column.sortValue) return column.sortValue(row);
  if (column.value) return column.value(row);
  return row?.[column.key];
}

function compareValues(first, second) {
  if (first == null && second == null) return 0;
  if (first == null) return 1;
  if (second == null) return -1;
  if (typeof first === 'number' && typeof second === 'number') return first - second;
  const firstDate = typeof first === 'string' ? Date.parse(first) : Number.NaN;
  const secondDate = typeof second === 'string' ? Date.parse(second) : Number.NaN;
  if (Number.isFinite(firstDate) && Number.isFinite(secondDate) && /[-T:]/.test(String(first))) return firstDate - secondDate;
  return String(first).localeCompare(String(second), undefined, { numeric: true, sensitivity: 'base' });
}

function csvCell(value) {
  const text = value == null ? '' : String(value);
  return `"${text.replaceAll('"', '""')}"`;
}

function downloadCsv(filename, rows, columns) {
  const exportColumns = columns.filter((column) => column.exportable !== false && column.key !== 'actions');
  const lines = [
    exportColumns.map((column) => csvCell(column.header)).join(','),
    ...rows.map((row) => exportColumns.map((column) => {
      const value = column.exportValue ? column.exportValue(row) : getSortValue(row, column);
      return csvCell(typeof value === 'object' ? JSON.stringify(value) : value);
    }).join(',')),
  ];
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function loadStoredView(tableId) {
  if (!tableId) return undefined;
  try {
    const raw = localStorage.getItem(`convoy:data-table:${tableId}`);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}

function TableMenu({ label, icon: Icon, children }) {
  return <details className="group relative">
    <summary className="flex h-9 cursor-pointer list-none items-center gap-2 border border-white/10 bg-white/[.025] px-3 text-[10px] font-bold uppercase tracking-[.12em] text-slate-400 transition hover:border-cyan-300/30 hover:text-cyan-100">
      <Icon size={13}/>{label}
    </summary>
    <div className="absolute right-0 top-[calc(100%+6px)] z-40 min-w-60 border border-white/10 bg-[#07101a]/98 p-2 shadow-2xl backdrop-blur-xl">
      {children}
    </div>
  </details>;
}

export function DataTable({
  rows = [],
  columns = [],
  getKey = defaultGetKey,
  onRowClick,
  tableId,
  searchPlaceholder = 'Search records',
  initialPageSize = 25,
  selectable = true,
  bulkActions = [],
  filters = [],
  toolbarActions,
  onRefresh,
  loading = false,
  emptyMessage = 'No records found',
  defaultSort = [],
  enableSavedViews = true,
  onSelectionChange,
  renderExpandedRow,
  manualPagination = false,
  totalRows,
  onPageChange,
  onPageSizeChange,
  className = '',
}) {
  const stored = useMemo(() => loadStoredView(tableId), [tableId]);
  const [search, setSearch] = useState(stored?.search ?? '');
  const [sorting, setSorting] = useState(stored?.sorting ?? defaultSort);
  const [visibility, setVisibility] = useState(stored?.visibility ?? {});
  const [density, setDensity] = useState(stored?.density ?? 'comfortable');
  const [pageSize, setPageSize] = useState(stored?.pageSize ?? initialPageSize);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [savedViews, setSavedViews] = useState(stored?.savedViews ?? []);
  const [viewName, setViewName] = useState('');
  const [columnWidths, setColumnWidths] = useState(stored?.columnWidths ?? {});
  const [pinned, setPinned] = useState(stored?.pinned ?? []);

  const visibleColumns = useMemo(() => {
    const values = columns.filter((column) => visibility[column.key] !== false);
    return [...values].sort((first, second) => {
      const firstPinned = pinned.indexOf(first.key);
      const secondPinned = pinned.indexOf(second.key);
      if (firstPinned >= 0 && secondPinned >= 0) return firstPinned - secondPinned;
      if (firstPinned >= 0) return -1;
      if (secondPinned >= 0) return 1;
      return columns.indexOf(first) - columns.indexOf(second);
    });
  }, [columns, pinned, visibility]);

  const filteredRows = useMemo(() => {
    if (manualPagination) return rows;
    const query = search.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => {
      const custom = columns.map((column) => column.searchValue?.(row)).filter(Boolean).join(' ');
      return `${asSearchText(row)} ${custom}`.toLowerCase().includes(query);
    });
  }, [columns, manualPagination, rows, search]);

  const sortedRows = useMemo(() => {
    if (manualPagination || !sorting.length) return filteredRows;
    return [...filteredRows].sort((first, second) => {
      for (const rule of sorting) {
        const column = columns.find((item) => item.key === rule.key);
        if (!column) continue;
        const result = compareValues(getSortValue(first, column), getSortValue(second, column));
        if (result !== 0) return rule.direction === 'desc' ? -result : result;
      }
      return 0;
    });
  }, [columns, filteredRows, manualPagination, sorting]);

  const rowTotal = manualPagination ? Number(totalRows ?? rows.length) : sortedRows.length;
  const pageCount = Math.max(1, Math.ceil(rowTotal / pageSize));
  const safePage = Math.min(page, pageCount);
  const pageRows = manualPagination
    ? sortedRows
    : sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const selectedRows = useMemo(
    () => rows.filter((row) => selected.has(String(getKey(row)))),
    [getKey, rows, selected],
  );
  const allPageSelected = pageRows.length > 0 && pageRows.every((row) => selected.has(String(getKey(row))));

  const pinnedOffsets = useMemo(() => {
    const result = {};
    let offset = selectable || renderExpandedRow ? 42 : 0;
    for (const column of visibleColumns) {
      if (!pinned.includes(column.key)) continue;
      result[column.key] = offset;
      offset += Number(columnWidths[column.key] ?? column.width ?? 180);
    }
    return result;
  }, [columnWidths, pinned, renderExpandedRow, selectable, visibleColumns]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  useEffect(() => {
    onSelectionChange?.(selectedRows);
  }, [onSelectionChange, selectedRows]);

  useEffect(() => {
    if (!tableId || !enableSavedViews) return;
    localStorage.setItem(`convoy:data-table:${tableId}`, JSON.stringify({
      search,
      sorting,
      visibility,
      density,
      pageSize,
      savedViews,
      columnWidths,
      pinned,
    }));
  }, [columnWidths, density, enableSavedViews, pageSize, pinned, savedViews, search, sorting, tableId, visibility]);

  const updatePage = (next) => {
    const value = Math.max(1, Math.min(pageCount, next));
    setPage(value);
    onPageChange?.(value);
  };

  const updatePageSize = (next) => {
    setPageSize(next);
    setPage(1);
    onPageSizeChange?.(next);
    onPageChange?.(1);
  };

  const toggleSort = (column, event) => {
    if (column.sortable === false) return;
    setPage(1);
    setSorting((current) => {
      const existing = current.find((item) => item.key === column.key);
      const nextDirection = !existing ? 'asc' : existing.direction === 'asc' ? 'desc' : undefined;
      const base = event?.shiftKey ? current.filter((item) => item.key !== column.key) : [];
      const next = nextDirection ? [...base, { key: column.key, direction: nextDirection }] : base;
      if (manualPagination) onPageChange?.(1, { sorting: next, search });
      return next;
    });
  };

  const startResize = (event, column) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const initial = Number(columnWidths[column.key] ?? column.width ?? 180);
    const move = (nextEvent) => {
      const width = Math.max(90, Math.min(620, initial + nextEvent.clientX - startX));
      setColumnWidths((current) => ({ ...current, [column.key]: width }));
    };
    const finish = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
  };

  const toggleSelected = (row) => {
    const id = String(getKey(row));
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const togglePage = () => {
    setSelected((current) => {
      const next = new Set(current);
      for (const row of pageRows) {
        const id = String(getKey(row));
        if (allPageSelected) next.delete(id); else next.add(id);
      }
      return next;
    });
  };

  const selectAllMatching = () => setSelected(new Set(sortedRows.map((row) => String(getKey(row)))));

  const toggleExpanded = (row) => {
    const id = String(getKey(row));
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const saveView = () => {
    const name = viewName.trim();
    if (!name) return;
    setSavedViews((current) => [
      ...current.filter((item) => item.name.toLowerCase() !== name.toLowerCase()),
      { name, search, sorting, visibility, density, pageSize, pinned, columnWidths },
    ].slice(-8));
    setViewName('');
  };

  const applyView = (view) => {
    setSearch(view.search ?? '');
    setSorting(view.sorting ?? []);
    setVisibility(view.visibility ?? {});
    setDensity(view.density ?? 'comfortable');
    setPageSize(view.pageSize ?? initialPageSize);
    setPinned(view.pinned ?? []);
    setColumnWidths(view.columnWidths ?? {});
    updatePage(1);
  };

  const activeFilterCount = filters.filter((filter) => filter.active !== false).length;
  const start = rowTotal === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = manualPagination
    ? Math.min(start + pageRows.length - 1, rowTotal)
    : Math.min(safePage * pageSize, rowTotal);

  return <div className={`flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden ${className}`}>
    <div className="flex shrink-0 flex-col gap-2 border-b border-white/8 bg-black/10 p-2.5 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1 lg:max-w-md">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-600"/>
          <Input
            value={search}
            onChange={(event) => {
              const value = event.target.value;
              setSearch(value);
              updatePage(1);
              if (manualPagination) onPageChange?.(1, { sorting, search: value });
            }}
            className="h-9 pl-9 pr-9 text-xs"
            placeholder={searchPlaceholder}
          />
          {search && <button type="button" onClick={() => setSearch('')} className="absolute right-2 top-1/2 grid size-6 -translate-y-1/2 place-items-center text-slate-600 hover:text-slate-200"><X size={13}/></button>}
        </div>
        {filters.length > 0 && <TableMenu label={`Filters${activeFilterCount ? ` ${activeFilterCount}` : ''}`} icon={Filter}>
          <div className="space-y-2">{filters.map((filter) => <div key={filter.key ?? filter.label} className="border border-white/8 bg-white/[.02] p-2.5">{filter.render ?? filter.label}</div>)}</div>
        </TableMenu>}
        <TableMenu label="Columns" icon={Columns3}>
          <div className="space-y-1">
            {columns.filter((column) => column.hideable !== false).map((column) => <div key={column.key} className="flex items-center gap-1">
              <button type="button" onClick={() => setVisibility((current) => ({ ...current, [column.key]: current[column.key] === false }))} className="flex min-w-0 flex-1 items-center justify-between px-2 py-2 text-left text-xs text-slate-400 hover:bg-white/[.04] hover:text-slate-100"><span className="truncate">{column.header}</span>{visibility[column.key] !== false && <Check size={13} className="text-cyan-300"/>}</button>
              <button type="button" title={pinned.includes(column.key) ? 'Unpin column' : 'Pin column'} onClick={() => setPinned((current) => current.includes(column.key) ? current.filter((key) => key !== column.key) : [...current, column.key])} className="grid size-7 place-items-center text-slate-700 hover:text-cyan-300">{pinned.includes(column.key) ? <PinOff size={12}/> : <Pin size={12}/>}</button>
            </div>)}
          </div>
        </TableMenu>
        <TableMenu label="View" icon={Eye}>
          <div className="space-y-2">
            <p className="px-2 text-[9px] font-black uppercase tracking-[.16em] text-slate-600">Density</p>
            {Object.keys(densityClasses).map((option) => <button key={option} type="button" onClick={() => setDensity(option)} className="flex w-full items-center justify-between px-2 py-2 text-xs capitalize text-slate-400 hover:bg-white/[.04] hover:text-slate-100"><span>{option}</span>{density === option && <Check size={13} className="text-cyan-300"/>}</button>)}
            {enableSavedViews && <>
              <div className="my-2 border-t border-white/8"/>
              <p className="px-2 text-[9px] font-black uppercase tracking-[.16em] text-slate-600">Saved views</p>
              {savedViews.map((view) => <div key={view.name} className="flex items-center gap-1"><button type="button" onClick={() => applyView(view)} className="min-w-0 flex-1 truncate px-2 py-2 text-left text-xs text-slate-400 hover:bg-white/[.04] hover:text-slate-100">{view.name}</button><button type="button" onClick={() => setSavedViews((current) => current.filter((item) => item.name !== view.name))} className="grid size-7 place-items-center text-slate-700 hover:text-rose-300"><Trash2 size={12}/></button></div>)}
              <div className="flex gap-1 p-1"><Input className="h-8 text-xs" value={viewName} onChange={(event) => setViewName(event.target.value)} placeholder="View name"/><button type="button" onClick={saveView} className="grid size-8 shrink-0 place-items-center border border-cyan-300/25 text-cyan-300 hover:bg-cyan-300/10"><Save size={13}/></button></div>
            </>}
          </div>
        </TableMenu>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {toolbarActions}
        {onRefresh && <Button variant="secondary" className="h-9 px-3 text-xs" disabled={loading} onClick={onRefresh}><RefreshCw size={13} className={loading ? 'animate-spin' : ''}/>Refresh</Button>}
        <Button variant="secondary" className="h-9 px-3 text-xs" onClick={() => downloadCsv(`${tableId ?? 'convoy-data'}-${new Date().toISOString().slice(0, 10)}.csv`, sortedRows, columns)}><Download size={13}/>Export</Button>
      </div>
    </div>

    {filters.some((filter) => filter.active !== false && filter.value) && <div className="flex flex-wrap gap-2 border-b border-white/8 px-3 py-2">
      {filters.filter((filter) => filter.active !== false && filter.value).map((filter) => <button key={filter.key ?? filter.label} type="button" onClick={filter.onClear} className="flex items-center gap-2 border border-cyan-300/15 bg-cyan-300/[.045] px-2.5 py-1 text-[10px] text-cyan-100"><span>{filter.label}: {filter.value}</span><X size={11}/></button>)}
    </div>}

    {selectedRows.length > 0 && <div className="flex flex-wrap items-center gap-2 border-b border-cyan-300/15 bg-cyan-300/[.035] px-3 py-2">
      <span className="text-[10px] font-black uppercase tracking-[.14em] text-cyan-200">{selectedRows.length} selected</span>
      {!manualPagination && selectedRows.length === pageRows.length && sortedRows.length > pageRows.length && <button type="button" onClick={selectAllMatching} className="text-[10px] font-bold text-cyan-300 hover:text-cyan-100">Select all {sortedRows.length} matches</button>}
      {bulkActions.map((action) => <Button key={action.label} variant={action.variant ?? 'secondary'} className="h-8 px-3 text-xs" onClick={() => action.onClick(selectedRows, () => setSelected(new Set()))}>{action.icon}{action.label}</Button>)}
      <button type="button" onClick={() => setSelected(new Set())} className="ml-auto text-[10px] text-slate-500 hover:text-slate-200">Clear</button>
    </div>}

    <div className="scrollbar min-h-0 flex-1 overflow-auto">
      <table className="w-full min-w-180 border-separate border-spacing-0 text-left text-sm">
        <thead className="bg-[#060c13] text-[9px] uppercase tracking-[.16em] text-slate-600">
          <tr>
            {(selectable || renderExpandedRow) && <th className="sticky left-0 z-20 w-11 border-b border-white/8 bg-[#060c13] px-3 py-3">{selectable && <input aria-label="Select current page" type="checkbox" checked={allPageSelected} onChange={togglePage} className="size-3.5 accent-cyan-300"/>}</th>}
            {visibleColumns.map((column) => {
              const rule = sorting.find((item) => item.key === column.key);
              const SortIcon = rule?.direction === 'asc' ? ArrowUp : rule?.direction === 'desc' ? ArrowDown : ArrowUpDown;
              const isPinned = pinned.includes(column.key);
              const width = Number(columnWidths[column.key] ?? column.width ?? 180);
              return <th key={column.key} style={{ width, minWidth: width, ...(isPinned ? { left: pinnedOffsets[column.key] } : {}) }} className={`sticky top-0 z-20 relative border-b border-white/8 bg-[#060c13] font-bold ${densityClasses[density]} ${isPinned ? 'sticky z-20 shadow-[8px_0_12px_-12px_rgba(0,0,0,.9)]' : ''} ${column.className ?? ''}`}>
                <button type="button" disabled={column.sortable === false} onClick={(event) => toggleSort(column, event)} className="inline-flex items-center gap-1.5 disabled:cursor-default"><span>{column.header}</span>{column.sortable !== false && <SortIcon size={11} className={rule ? 'text-cyan-300' : 'text-slate-700'}/>} {rule && sorting.length > 1 && <span className="font-mono text-[8px] text-cyan-300">{sorting.findIndex((item) => item.key === column.key) + 1}</span>}</button>
                {column.resizable !== false && <button type="button" aria-label={`Resize ${column.header}`} onPointerDown={(event) => startResize(event, column)} className="absolute right-0 top-0 grid h-full w-2 cursor-col-resize place-items-center text-transparent hover:text-cyan-300"><GripVertical size={10}/></button>}
              </th>;
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/6">
          {loading && rows.length === 0 ? Array.from({ length: 5 }).map((_, index) => <tr key={index} className="animate-pulse"><td colSpan={visibleColumns.length + (selectable || renderExpandedRow ? 1 : 0)} className="px-4 py-4"><div className="h-5 bg-white/[.04]"/></td></tr>) : pageRows.map((row) => {
            const rowId = String(getKey(row));
            const checked = selected.has(rowId);
            const opened = expanded.has(rowId);
            return <RowFragment key={rowId}>
              <tr onClick={onRowClick ? () => onRowClick(row) : undefined} className={`transition ${checked ? 'bg-cyan-300/[.045]' : 'hover:bg-white/[.025]'} ${onRowClick ? 'cursor-pointer' : ''}`}>
                {(selectable || renderExpandedRow) && <td className="sticky left-0 z-10 w-11 bg-[#07101a] px-3 py-3" onClick={(event) => event.stopPropagation()}><div className="flex items-center gap-1">{renderExpandedRow && <button type="button" onClick={() => toggleExpanded(row)} className="grid size-5 place-items-center text-slate-600 hover:text-cyan-300"><ChevronRight size={12} className={opened ? 'rotate-90' : ''}/></button>}{selectable && <input aria-label="Select row" type="checkbox" checked={checked} onChange={() => toggleSelected(row)} className="size-3.5 accent-cyan-300"/>}</div></td>}
                {visibleColumns.map((column) => {
                  const isPinned = pinned.includes(column.key);
                  const width = Number(columnWidths[column.key] ?? column.width ?? 180);
                  return <td key={column.key} style={{ width, minWidth: width, ...(isPinned ? { left: pinnedOffsets[column.key] } : {}) }} className={`${densityClasses[density]} ${isPinned ? 'sticky z-10 bg-[#07101a] shadow-[8px_0_12px_-12px_rgba(0,0,0,.9)]' : ''} ${column.className ?? ''}`}>{column.cell ? column.cell(row) : String(row?.[column.key] ?? '—')}</td>;
                })}
              </tr>
              {opened && renderExpandedRow && <tr><td colSpan={visibleColumns.length + 1} className="border-y border-cyan-300/10 bg-cyan-300/[.02] p-4">{renderExpandedRow(row)}</td></tr>}
            </RowFragment>;
          })}
        </tbody>
      </table>
      {!loading && pageRows.length === 0 && <div className="grid min-h-44 place-items-center px-5 text-center"><div><ListFilter size={24} className="mx-auto text-slate-700"/><p className="mt-3 text-sm font-semibold text-slate-300">{emptyMessage}</p><p className="mt-1 text-xs text-slate-600">Adjust the search or filters.</p></div></div>}
    </div>

    <div className="flex shrink-0 flex-col gap-2 border-t border-white/8 bg-black/10 px-3 py-2 text-[10px] text-slate-500 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3"><span>{start}-{Math.max(start, end)} of {rowTotal}</span><label className="flex items-center gap-2"><Rows3 size={12}/><select value={pageSize} onChange={(event) => updatePageSize(Number(event.target.value))} className="h-7 border border-white/10 bg-[#07101a] px-2 text-[10px] text-slate-300"><option value={15}>15</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option></select></label></div>
      <div className="flex items-center gap-2"><span>Page {safePage} / {pageCount}</span><button type="button" disabled={safePage <= 1} onClick={() => updatePage(safePage - 1)} className="grid size-7 place-items-center border border-white/10 text-slate-400 disabled:opacity-30"><ChevronLeft size={13}/></button><button type="button" disabled={safePage >= pageCount} onClick={() => updatePage(safePage + 1)} className="grid size-7 place-items-center border border-white/10 text-slate-400 disabled:opacity-30"><ChevronRight size={13}/></button></div>
    </div>
  </div>;
}

function RowFragment({ children }) {
  return children;
}
